<script setup lang="ts">
import type { ProductResponseDTO } from '~/models/product-response-dto';
import { useCellphone, useNavigation } from '~~/composables/states';

const route = useRoute();
const navigation = useNavigation();
const cellphone = useCellphone();
const merchantName = ref('');
const navExpanded = ref(false);
const pending = ref(false);

const { data } = useLazyFetch<ProductResponseDTO>(`/api/workflow/${route.params.id}/product`);
watch(data, (newData) => {
    if (newData) {
        merchantName.value = newData.merchantName || '';
        cellphone.value = newData.mobileNumber || '';
        console.log('Merchant Name:', merchantName.value);
    }
}, { immediate: true });
function getCurrentStep() {
    return navigation.value.find(m => m.status === 'current');
}
function toggleExpanded() {
    navExpanded.value = !navExpanded.value
}

async function sendDirectURL() {
    if (!cellphone.value) {
        console.error('Cellphone number is not set');
        return;
    }
    const url = `${window.location.origin}${route.fullPath}`;
    const body = JSON.stringify({
        mobileNumber: cellphone.value,
        url: url,
        customerName: ''
    });

    await $fetch(`/api/communications/`, {
        method: 'POST',
        body: body
    });
}
</script>

<template>
    <nav v-if="!pending"
         aria-label="Progress">
        <div class="lg:hidden flex flex-row items-center justify-between px-4"
             @click="toggleExpanded">
            <span class="flex min-w-0 flex-col">
                <span class="text-sm font-medium text-secondary">{{
                    getCurrentStep()?.name }}</span>
                <span class="text-sm text-gray-500">{{
                    getCurrentStep()?.description }}</span>
            </span>
            <Icon name="material-symbols:arrow-circle-down"
                  size="1.5em"
                  style="color: rgb(var(--color-secondary))" />
        </div>
        <div :class="[navExpanded ? 'step-nav-active' : '', 'step-nav']">
            <ol role="list"
                class="overflow-hidden p-4">
                <template v-for="(step, stepIdx) in navigation.filter(n => !n.isAutomated)"
                          :key="step.name">
                    <!-- Regular Step -->
                    <li
                        :class="[stepIdx !== navigation.length - 1 ? 'pb-10' : '', 'relative']">
                        <!-- Complete Step -->
                        <template v-if="step.status === 'complete'">
                            <div v-if="stepIdx !== navigation.length - 1"
                                 class="absolute top-4 left-4 -ml-px mt-0.5 h-full w-0.5 bg-nav"
                                 aria-hidden="true" />
                            <a :href="step.href"
                               class="group relative flex items-start">
                                <span class="flex h-9 items-center">
                                    <span
                                          class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-nav group-hover:bg-nav">
                                        <Icon class="h-5 w-5 text-white"
                                              aria-hidden="true"
                                              name="heroicons:check" />
                                    </span>
                                </span>
                                <span class="ml-4 flex min-w-0 flex-col">
                                    <span class="text-sm font-medium">{{
                                        step.name }}</span>
                                    <span class="text-sm text-gray-500">{{
                                        step.description }}</span>
                                </span>
                            </a>
                        </template>

                        <!-- Current Step -->
                        <template v-else-if="step.status === 'current'">
                            <div v-if="stepIdx !== navigation.length - 1"
                                 class="absolute top-4 left-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                                 aria-hidden="true" />
                            <a :href="step.href"
                               class="group relative flex items-start"
                               aria-current="step">
                                <span class="flex h-9 items-center"
                                      aria-hidden="true">
                                    <span
                                          class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-secondary bg-white">
                                        <span
                                              class="h-2.5 w-2.5 rounded-full bg-nav" />
                                    </span>
                                </span>
                                <span class="ml-4 flex min-w-0 flex-col">
                                    <span
                                          class="text-sm font-medium text-secondary">{{
                                            step.name }}</span>
                                    <span class="text-sm text-gray-500">{{
                                        step.description }}</span>
                                </span>
                            </a>
                        </template>

                        <!-- Upcoming Step -->
                        <template v-else>
                            <div v-if="stepIdx !== navigation.length - 1"
                                 class="absolute top-4 left-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                                 aria-hidden="true" />
                            <a :href="step.href"
                               class="group relative flex items-start">
                                <span class="flex h-9 items-center"
                                      aria-hidden="true">
                                    <span
                                          class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400">
                                        <span
                                              class="h-2.5 w-2.5 rounded-full bg-transparent group-hover:bg-gray-300" />
                                    </span>
                                </span>
                                <span class="ml-4 flex min-w-0 flex-col">
                                    <span
                                          class="text-sm font-medium text-gray-500">{{
                                            step.name }}</span>
                                    <span class="text-sm text-gray-500">{{
                                        step.description }}</span>
                                </span>
                            </a>
                        </template>
                    </li>

                    <!-- Address Information Nav Item -->
                    <template v-if="step.showHandoverButton">
                        <li class="relative pb-10">
                            <div class="absolute top-4 left-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                                 aria-hidden="true" />
                            <div class="group relative flex items-start">
                                <span class="flex h-9 items-center"
                                      aria-hidden="true">
                                    <span
                                          class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                                        <span
                                              class="h-2.5 w-2.5 rounded-full bg-transparent" />
                                    </span>
                                </span>
                                <div class="ml-4 flex min-w-0 flex-col">
                                    <template v-if="step.showHandoverButton">
                                        <button class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                                                @click="sendDirectURL">
                                            Hand Over to Customer
                                        </button>
                                    </template>
                                </div>
                            </div>
                        </li>
                    </template>
                </template>
                <div v-if="merchantName === 'Mondo' || merchantName === 'Clientele'"
                     class="pt-4">
                    <div class="px-4 pb-4 border-t border-gray-200 pt-4">
                        <p class="text-sm"
                           style="color: rgb(var(--color-secondary))">Onboarding
                            Journey
                            Reference:</p>
                        <p class="text-sm font-medium text-gray-900">{{
                            route.params.id
                        }}</p>
                    </div>
                </div>

            </ol>
        </div>
    </nav>
    <div v-else>
        loading
    </div>
</template>

<style scoped>
.step-nav {
    max-height: 0px;
    padding: 0.5rem;
}

.step-nav>ol {
    opacity: 0;
    max-height: 0px;
}

.step-nav.step-nav-active {
    max-height: 1000px;
    transition: 0.4s all ease-in-out;
}

.step-nav.step-nav-active>ol {
    opacity: 1;
    max-height: 1000px;
    transition: 0.4s all ease-in-out;
}

@media screen and (min-width: 1024px) {
    .step-nav {
        height: auto;
        max-height: 1000px;
    }

    .step-nav>ol {
        opacity: 1;
        max-height: 1000px;
    }
}
</style>